import React, { useState, Fragment, useEffect } from 'react';
import Localize from 'react-intl-universal';

import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { httpStatusCodes } from '@common/Constants';
import { formatDateTimeStringToDateTimeObject } from '@common/Util';
import RichTextEditor from '@components/RichTextEditor/RichTextEditor';
import useDownload from '@hooks/Download/useDownload.js';
import { getByPathAndParams } from '@services/BaseApi';

import { newsImageUrl, readNewsDescriptionJSON, readNewsDescription } from './Util';

const StyledBox = styled(Box)(() => ({
  paddingLeft: 0,
  paddingRight: 0,
  marginTop: '2rem'
}));

const StyledImgBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  background: theme.palette.grey[300],
  height: '13rem',
  [theme.breakpoints.up('md')]: {
    height: '17rem'
  }
}));

const StyledLinkBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  marginTop: '1rem',
  marginBottom: '1rem',
  [theme.breakpoints.up('xl')]: {
    flexDirection: 'row'
  }
}));

const StyledDialogContent = styled(DialogContent)(() => ({
  paddingTop: 0,
  minHeight: '15rem',
  paddingLeft: '3.5rem',
  paddingRight: '3.5rem'
}));

const StyledImg = styled('img')(({ theme, props }) => ({
  border: props.hasImage ? 0 : `4px solid ${theme.palette.grey[200]}`,
  borderRadius: '5px',
  height: '13rem',
  margin: props.hasImage ? 0 : '1rem',
  background: theme.palette.grey[300],
  objectFit: props.hasImage ? 'cover' : 'unset',
  width: props.hasImage ? '100%' : 'unset',
  [theme.breakpoints.up('md')]: {
    height: props.hasImage ? '17rem' : '15rem'
  }
}));

const NewsDialog = ({ news, memoizedImageSource }) => {
  const [open, setOpen] = useState(false);
  const [downloadData, setDownloadData] = useState();

  const { downloadFile } = useDownload(downloadData);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDownload = async () => {
    try {
      const response = await getByPathAndParams({
        path: news?.fileURL,
        temApi: true
      });

      if (response?.status === httpStatusCodes.ok) {
        setDownloadData(response);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    downloadFile();
  }, [downloadData]);

  return (
    <Fragment>
      <Button size='small' color='primary' onClick={handleClickOpen}>
        {Localize.get('HomePage.readNews')}
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        fullWidth={true}
        maxWidth='md'
      >
        <StyledImgBox>
          <StyledImg
            props={{ hasImage: news?.catalogPicture?.url }}
            src={`${newsImageUrl(news, memoizedImageSource)}`}
            alt=''
            loading='lazy'
          />
        </StyledImgBox>

        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <StyledDialogContent>
          {
            <Box sx={{ mt: 1 }}>
              <Typography gutterBottom variant='h5' component='div'>
                {readNewsDescription(news)}
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {formatDateTimeStringToDateTimeObject(news.validFrom)}
              </Typography>
              <StyledBox>
                <RichTextEditor
                  initialValue={
                    readNewsDescriptionJSON(news) || Localize.get('EventDetails.noTopics')
                  }
                  disabled
                  isToolbarHidden
                  customStyles={{ minHeight: '50px' }}
                />
              </StyledBox>
              <StyledLinkBox>
                {news?.url && (
                  <Link target='_blank' href={news?.url} underline='none'>
                    {news?.url}
                  </Link>
                )}
                {news?.fileURL && (
                  <Button
                    sx={{ maxWidth: '12rem', marginTop: '1rem' }}
                    variant='outlined'
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload()}
                  >
                    {Localize.get('HomePage.download')}
                  </Button>
                )}
              </StyledLinkBox>
            </Box>
          }
        </StyledDialogContent>
      </Dialog>
    </Fragment>
  );
};

export default NewsDialog;
